import React, { useState, useEffect } from 'react';

import { useDictionary, usePage, useAuth } from '@/src/app/hooks';
import { Loading, ErrorMessage, Container, type UnitDTO } from '@/src/ui';
import { apiRequest } from '@/src/services';
import './DashboardPage.css';

import GTKCover from '@/src/assets/img/lessons/cover-grade-TK.jpg';
import GKCover from '@/src/assets/img/lessons/cover-grade-K.jpg';
import G1Cover from '@/src/assets/img/lessons/cover-grade-1.jpg';
import G2Cover from '@/src/assets/img/lessons/cover-grade-2.jpg';
import G3Cover from '@/src/assets/img/lessons/cover-grade-3.jpg';
import G4Cover from '@/src/assets/img/lessons/cover-grade-4.jpg';
import G5Cover from '@/src/assets/img/lessons/cover-grade-5.jpg';
import G6Cover from '@/src/assets/img/lessons/cover-grade-6.jpg';
import G6MCover from '@/src/assets/img/lessons/cover-grade-6M.jpg';
import G7Cover from '@/src/assets/img/lessons/cover-grade-7.jpg';
import G8Cover from '@/src/assets/img/lessons/cover-grade-8.jpg';

function getCoverImage(grade: string): string {
    switch (grade) {
        case 'TK':
            return GTKCover;
        case 'K':
            return GKCover;
        case '1':
            return G1Cover;
        case '2':
            return G2Cover;
        case '3':
            return G3Cover;
        case '4':
            return G4Cover;
        case '5':
            return G5Cover;
        case '6':
            return G6Cover;
        case '6M':
            return G6MCover;
        case '7':
            return G7Cover;
        case '8':
            return G8Cover;
        default:
            return '';
    }
}

const DashboardPage: React.FC = () => {
    const { translate } = useDictionary();
    document.title = translate('Dashboard', 'Dashboard');
    const { setSubheaderTitle, setSubheaderType, pageGrade, pageLanguage } = usePage();
    const { user, removeMyLesson } = useAuth();
    const myLessons = user?.myLessons || [];

    useEffect(() => {
        setSubheaderTitle(translate('Dashboard'));
        setSubheaderType('default');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate]);

    const [unitsData, setUnitsData] = useState<UnitDTO[] | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async function fetchData() {
            setError('');
            setLoading(true);
            setUnitsData(null);
            try {
                const res = await apiRequest<UnitDTO[]>('GET', `dbo/lessons?grade=${pageGrade}`);
                setUnitsData(res.data);
            } catch {
                setError('Failed to fetch data');
            } finally {
                setLoading(false);
            }
        })();
    }, [pageGrade, pageLanguage]);

    const isLocal = window.location.hostname === 'localhost';

    return (
        <Container>
            <ErrorMessage error={error} />
            {loading ? (
                <Loading />
            ) : (
                <div className="container dashboard-container" id="dashboard">
                    <div className="row">
                        {/* Left Column (Classroom, Assessments, PL, Help) */}
                        <div className="col-12 col-sm-6 order-2 order-sm-1" id="left-column">
                            <div id="classroomBox" className="dashboard-box pd-grey background">
                                <div className="dashboard-labels pd-primary text">
                                    {translate('Classroom')}
                                </div>
                                <div className="dashboard-inner background pd-light-grey">
                                    <p>
                                        Connect with students, assign lessons, and track their
                                        progress.
                                    </p>
                                    <button
                                        className="background pd-primary"
                                        onClick={() => {
                                            window.location.href = isLocal
                                                ? 'http://localhost:5173/'
                                                : 'https://classroom.paradigmmath.com/';
                                        }}
                                        type="button"
                                    >
                                        {translate('Classroom')}
                                    </button>
                                </div>
                            </div>
                            <div id="assessmentsBox" className="dashboard-box pd-grey background">
                                <div className="dashboard-labels pd-primary text">
                                    {translate('Assessments')}
                                </div>
                                <div className="dashboard-inner background pd-light-grey">
                                    <p>Quick access to view/print assessment PDFs and keys.</p>
                                    <button
                                        className="background pd-primary"
                                        onClick={() => {
                                            window.location.href = `/resources/assessments${pageGrade ? `/${pageGrade}` : ''}`;
                                        }}
                                        type="button"
                                    >
                                        {translate('View Assessments')}
                                    </button>
                                </div>
                            </div>
                            <div
                                id="professionalLearningBox"
                                className="dashboard-box pd-grey background"
                            >
                                <div className="dashboard-labels pd-primary text">
                                    {translate('Professional Learning')}
                                </div>
                                <div className="dashboard-inner background pd-light-grey">
                                    <p>Videos, Webinars, and Inservice Handouts</p>
                                    <button
                                        className="background pd-primary"
                                        onClick={() => {
                                            window.location.href =
                                                '/resources/professionalLearning';
                                        }}
                                        type="button"
                                    >
                                        {translate('Learn More')}
                                    </button>
                                </div>
                            </div>
                            <a
                                id="helpBox"
                                href="/user/userGuides"
                                className="dashboard-box pd-grey background"
                                tabIndex={0}
                                role="link"
                                style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                }}
                            >
                                <div className="dashboard-labels pd-primary text">
                                    {translate('Help')}
                                </div>
                            </a>
                        </div>

                        {/* Right Column (My Lessons) */}
                        <div
                            className="col-12 col-sm-6 right-column order-1 order-sm-2"
                            id="right-column"
                        >
                            <div id="myLessonsBox" className="dashboard-box pd-grey background">
                                <div className="dashboard-labels pd-primary text">
                                    {translate('My Lessons')}
                                </div>
                                <div className="dashboard-inner background pd-light-grey overflow-auto">
                                    {!myLessons?.length
                                        ? 'No lessons saved.'
                                        : myLessons.map((lessonId) => {
                                              const gradeUnitLesson = lessonId.split('_');
                                              if (!gradeUnitLesson || unitsData === null) {
                                                  return;
                                              }

                                              let grade = gradeUnitLesson[0];
                                              grade = grade.slice(1);
                                              let unitNum = gradeUnitLesson[1];
                                              unitNum = unitNum.slice(1);
                                              let lessonIndex = gradeUnitLesson[2];
                                              lessonIndex = lessonIndex.slice(1);

                                              const unitData = unitsData?.find(
                                                  (unit) => unit.unitNumber === parseInt(unitNum)
                                              );
                                              const lessonData =
                                                  unitData?.lessons?.[parseInt(lessonIndex)];

                                              if (!lessonData) {
                                                  <div>Lesson data not found for {lessonId}</div>;
                                                  return;
                                              }

                                              return (
                                                  <a
                                                      key={`dashboard-lesson-${lessonId}`}
                                                      className={`lesson-box background pd-G${grade}-primary`}
                                                      href={`/allLessons/${grade}/${unitNum}/${lessonIndex}`}
                                                  >
                                                      <img
                                                          src={getCoverImage(grade)}
                                                          alt="Lesson Image"
                                                          className="lesson-image"
                                                      />
                                                      <div className="lesson-content">
                                                          <div className="unit-title unit-number">
                                                              Unit {unitNum}
                                                          </div>
                                                          <div className="lesson-title lesson-pagelinks">
                                                              Lesson {lessonData.lessonNumber}:{' '}
                                                              {lessonData.title}
                                                          </div>
                                                      </div>
                                                      <button
                                                          className="btn-close btn-close-white"
                                                          aria-label="close"
                                                          onClick={(e) => {
                                                              e.stopPropagation();
                                                              e.preventDefault();
                                                              removeMyLesson(lessonId);
                                                          }}
                                                      >
                                                          &times;
                                                      </button>
                                                  </a>
                                              );
                                          })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default DashboardPage;
